export const EXTERNAL_ANONYMOUS_PROPERTIES = {
  auth_state: 'anonymous',
  traffic_type: 'external',
} as const

export const EXTERNAL_AUTHENTICATED_PROPERTIES = {
  auth_state: 'authenticated',
  traffic_type: 'external',
} as const

export type PostHogIdentityCaptureState = 'pending' | 'allowed' | 'denied'

let identityCaptureState: PostHogIdentityCaptureState = 'pending'
let identityCaptureGeneration = 0

export const getPostHogIdentityCaptureState = () => identityCaptureState

export const setPostHogIdentityCapturePending = () => {
  identityCaptureState = 'pending'
  identityCaptureGeneration += 1
  return identityCaptureGeneration
}

export const isPostHogIdentityCaptureCurrent = (generation: number) =>
  generation === identityCaptureGeneration

export const setPostHogIdentityCaptureAllowed = (allowed: boolean) => {
  identityCaptureState = allowed ? 'allowed' : 'denied'
}

export const filterPostHogCaptureByIdentityGate = <T>(captureResult: T | null): T | null =>
  identityCaptureState === 'allowed' ? captureResult : null

const INTERNAL_ACCOUNT_EMAIL_SHA256 = new Set([
  '23a544ce8bcd651926ed9202dfd66f390b7ca1bed6b85912920e2b1bedc2bcc1',
  'f53f84f71e2f6d0b7d45927b34888ff6d69688360f25e975509097a844f63473',
  'bdd21a76ef1e82d4adcc88485485e0b90fb106c0ff01eb68ce9c3e9567715379',
  '179a9546043a8003aeb9b53de54721f10d342e5167a40c2bb40ba0ed1034b3f3',
  'e7f50c74ceb8461546feb9bcbe65814fe2fd4cb67e624fd5e1107a27dd7569a0',
])

export type PostHogIdentityUser = {
  email?: unknown
  name?: unknown
  primaryEmail?: unknown
  sub?: unknown
  username?: unknown
}

export type PostHogIdentityClient = {
  get_identified_user_id: () => Promise<unknown>
  get_explicit_consent_status: () =>
    | 'granted'
    | 'denied'
    | 'pending'
    | Promise<'granted' | 'denied' | 'pending'>
  identify: (distinctId: string, properties?: Record<string, unknown>) => void | Promise<void>
  is_client_loaded: () => boolean
  is_identity_capture_current: (generation: number) => boolean
  opt_in_capturing: (options?: { captureEventName?: string | null | false }) => void | Promise<void>
  opt_out_capturing: () => void | Promise<void>
  register: (properties: Record<string, unknown>) => void | Promise<void>
  reset: () => void | Promise<void>
  clear_pending_captures: () => void
  flush_pending_captures: () => void
  set_identity_capture_allowed: (allowed: boolean) => void
  set_identity_capture_pending: () => number
  startSessionRecording: () => void | Promise<void>
  stopSessionRecording: () => void | Promise<void>
}

type InternalAccountMatcher = (user: PostHogIdentityUser | null | undefined) => Promise<boolean>

const normalizeText = (value: unknown) => String(value || '').trim()

const getPostHogIdentityEmail = (user: PostHogIdentityUser) =>
  (normalizeText(user.email) || normalizeText(user.primaryEmail)).toLowerCase()

const sha256 = async (value: string) => {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(value))
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('')
}

type PostHogConsentStatus = 'granted' | 'denied' | 'pending'

const restorePostHogConsent = async (
  posthog: PostHogIdentityClient,
  consentStatus: PostHogConsentStatus,
) => {
  if (consentStatus === 'denied') {
    await posthog.opt_out_capturing()
  } else if (consentStatus === 'granted') {
    await posthog.opt_in_capturing({ captureEventName: false })
  }
}

const resetPostHogPreservingConsent = async (posthog: PostHogIdentityClient) => {
  const consentStatus = await posthog.get_explicit_consent_status()
  await posthog.reset()
  await restorePostHogConsent(posthog, consentStatus)
  return consentStatus
}

const applyExternalCaptureConsent = async (posthog: PostHogIdentityClient, generation: number) => {
  const consentStatus = await posthog.get_explicit_consent_status()
  if (!posthog.is_identity_capture_current(generation)) return null
  if (consentStatus === 'denied') {
    posthog.set_identity_capture_allowed(false)
    posthog.clear_pending_captures()
    await posthog.stopSessionRecording()
    if (!posthog.is_identity_capture_current(generation)) return null
  }
  return consentStatus
}

const finishExternalCaptureSync = async (
  posthog: PostHogIdentityClient,
  consentStatus: PostHogConsentStatus,
  generation: number,
) => {
  if (consentStatus === 'denied' || !posthog.is_identity_capture_current(generation)) {
    return
  }

  // Start only after identity/register calls have been queued and the gate is
  // open, so rrweb's first full snapshot and early custom events are retained.
  posthog.set_identity_capture_allowed(true)
  await posthog.startSessionRecording()
  if (!posthog.is_identity_capture_current(generation)) return
  posthog.flush_pending_captures()
}

export const isInternalPostHogAccount = async (user: PostHogIdentityUser | null | undefined) => {
  const email = user ? getPostHogIdentityEmail(user) : ''
  if (!email) return false

  // Fail closed if hashing is unavailable: avoiding analytics is safer than
  // accidentally capturing an internal account on an obsolete browser.
  if (!globalThis.crypto?.subtle) return true

  try {
    return INTERNAL_ACCOUNT_EMAIL_SHA256.has(await sha256(email))
  } catch {
    return true
  }
}

export const syncPostHogIdentity = async (
  posthog: PostHogIdentityClient,
  user: PostHogIdentityUser | null,
  isInternalAccount: InternalAccountMatcher = isInternalPostHogAccount,
  requestedGeneration?: number,
) => {
  // Synchronously block SDK autocapture and queue facade captures while the
  // asynchronous internal-account decision is in flight.
  const generation = requestedGeneration ?? posthog.set_identity_capture_pending()
  if (!posthog.is_identity_capture_current(generation)) return
  await posthog.stopSessionRecording()
  if (!posthog.is_identity_capture_current(generation)) return

  if (!user) {
    const currentIdentifiedUserId = normalizeText(await posthog.get_identified_user_id())
    if (!posthog.is_identity_capture_current(generation)) return
    if (currentIdentifiedUserId) {
      await resetPostHogPreservingConsent(posthog)
      if (!posthog.is_identity_capture_current(generation)) return
    }
    const consentStatus = await applyExternalCaptureConsent(posthog, generation)
    if (!consentStatus) return
    await posthog.register(EXTERNAL_ANONYMOUS_PROPERTIES)
    if (!posthog.is_identity_capture_current(generation)) return
    await finishExternalCaptureSync(posthog, consentStatus, generation)
    return
  }

  const isInternal = await isInternalAccount(user)
  if (!posthog.is_identity_capture_current(generation)) return
  if (isInternal) {
    // Internal-account filtering is an application policy, not a user consent
    // decision. Reset identity but preserve the browser's explicit consent.
    if (posthog.is_client_loaded()) {
      await resetPostHogPreservingConsent(posthog)
      if (!posthog.is_identity_capture_current(generation)) return
    }
    posthog.set_identity_capture_allowed(false)
    posthog.clear_pending_captures()
    return
  }

  const email = getPostHogIdentityEmail(user)
  const distinctId = email
  const currentIdentifiedUserId = normalizeText(await posthog.get_identified_user_id())
  if (!posthog.is_identity_capture_current(generation)) return
  if (currentIdentifiedUserId && currentIdentifiedUserId !== distinctId) {
    await resetPostHogPreservingConsent(posthog)
    if (!posthog.is_identity_capture_current(generation)) return
  }

  const consentStatus = await applyExternalCaptureConsent(posthog, generation)
  if (!consentStatus) return
  await posthog.register(EXTERNAL_AUTHENTICATED_PROPERTIES)
  if (!posthog.is_identity_capture_current(generation)) return

  if (distinctId) {
    await posthog.identify(distinctId, {
      email,
      name: user.name,
      username: user.username,
    })
  }
  if (!posthog.is_identity_capture_current(generation)) return
  await finishExternalCaptureSync(posthog, consentStatus, generation)
}

export const syncPostHogLogout = async (
  posthog: PostHogIdentityClient,
  requestedGeneration?: number,
) => {
  // Logout may race with autocapture too, so close the gate and queue facade
  // captures until the next anonymous identity is ready.
  const generation = requestedGeneration ?? posthog.set_identity_capture_pending()
  if (!posthog.is_identity_capture_current(generation)) return
  await posthog.stopSessionRecording()
  if (!posthog.is_identity_capture_current(generation)) return

  // Always reset even when app storage is already empty: PostHog persistence
  // may still contain a stale identified user after partial auth cleanup.
  const consentStatus = await resetPostHogPreservingConsent(posthog)
  if (!posthog.is_identity_capture_current(generation)) return
  const isCaptureAllowed = consentStatus !== 'denied'
  if (!isCaptureAllowed) {
    posthog.set_identity_capture_allowed(false)
    posthog.clear_pending_captures()
  }
  await posthog.register(EXTERNAL_ANONYMOUS_PROPERTIES)
  if (!posthog.is_identity_capture_current(generation)) return
  await finishExternalCaptureSync(posthog, consentStatus, generation)
}
